import type { Investigation, HomepageFeed } from '@/domains/types'

export interface HomeCategoryRail {
  /** The category term's id: rails are grouped by term, never by label. */
  termId: string
  /** The term's label in the displayed language. */
  label: string
  items: Investigation[]
}

export interface HomeSections {
  lead: Investigation | null
  headlines: Investigation[]
  rails: HomeCategoryRail[]
  river: Investigation[]
}

const MIN_RAIL_ITEMS = 3
const MAX_RAIL_ITEMS = 4
const DEFAULT_MAX_RAILS = 4

/**
 * Groups `feed.latest` by category TERM (its id, so two terms that happen to
 * share a label stay two rails and one term stays one rail whatever its label)
 * client-side, and keeps the top categories by item count. A category with
 * fewer than `MIN_RAIL_ITEMS` items is dropped rather than shown thin.
 */
export function selectHomeSections(
  feed: HomepageFeed | undefined,
  options: { maxRails?: number } = {},
): HomeSections {
  if (!feed) {
    return { lead: null, headlines: [], rails: [], river: [] }
  }

  const maxRails = options.maxRails ?? DEFAULT_MAX_RAILS
  const groups = new Map<string, { label: string; items: Investigation[] }>()
  for (const investigation of feed.latest) {
    for (const term of investigation.categoryTerms ?? []) {
      const group = groups.get(term.id) ?? { label: term.label, items: [] }
      group.items.push(investigation)
      groups.set(term.id, group)
    }
  }

  const rails: HomeCategoryRail[] = Array.from(groups.entries())
    .filter(([, group]) => group.items.length >= MIN_RAIL_ITEMS)
    .sort((left, right) => right[1].items.length - left[1].items.length)
    .slice(0, maxRails)
    .map(([termId, group]) => ({
      termId,
      label: group.label,
      items: group.items.slice(0, MAX_RAIL_ITEMS),
    }))

  return {
    lead: feed.lead,
    headlines: feed.headlines,
    rails,
    river: feed.latest,
  }
}
